import { SentryFetchHttpHandler } from "@amzn/sentry-fetch-http-handler";
import { OkapiIssuerClient, RegisterIdentityCommand, GetIssuerStateCommand } from "@amzn/piezo-okapiissuer-typescript-client";
import { createInlineKeyPair } from '@amzn/piezo-plenum-typescript';
import { bytesToEncoded } from '../byteUtils.js';
import { PIEZO_ENDPOINTS } from '../__generated__/piezo-endpoints.js';
import { createSigningNameOverrideProvider } from './signingUtils.js';
const OKAPI_RIP_STAGES = ['gamma', 'prod'];
const OKAPI_PREPROD_REGIONS = ['us-west-2', 'eu-central-1', 'ap-northeast-1', 'us-east-2'];
const OKAPI_PROD_REGIONS = [
    'ap-northeast-1',
    'eu-central-1',
    'eu-west-3',
    'us-east-2',
    'us-west-2',
];
export class PiezoOkapiThickClient {
    crypto;
    plenumClient;
    okapiClients;
    constructor(crypto, plenumClient, props) {
        this.crypto = crypto;
        this.plenumClient = plenumClient;
        this.okapiClients = {};
        if (props.clientOverride) {
            const client = this.getOkapiClient(props.authType, props.clientOverride.region, props.clientOverride.endpoint, props.credentials);
            this.okapiClients[props.clientOverride.endpoint] = client;
        }
        else if (props.region && OKAPI_RIP_STAGES.includes(props.stage)) {
            // Use build-time generated RIP endpoints for the specified region
            const region = props.region;
            const generatedConfig = PIEZO_ENDPOINTS[props.stage]?.[props.authType]?.[region];
            if (generatedConfig && generatedConfig.okapiIssuers.length > 0) {
                for (const issuer of generatedConfig.okapiIssuers) {
                    this.okapiClients[issuer.url] = this.getOkapiClient(props.authType, region, issuer.url, props.credentials, issuer.signingName);
                }
            }
            else {
                throw new Error(`[Okapi] No generated endpoints found for ${props.stage}/${props.authType}/${region}. Ensure that your version set has latest RIP data.`);
            }
        }
        else {
            // Default: static endpoint templates (no region specified)
            this.getRegions(props.stage).forEach((region) => {
                const endpoint = this.getEndpoint(props.authType, props.stage, region);
                this.okapiClients[endpoint] = this.getOkapiClient(props.authType, region, endpoint, props.credentials);
            });
        }
    }
    /**
     * Registers the current identity with all okapi issuers in the given stage with the provided keyProvider
     *
     * @argument keyProvider: The key provider to register the identity with. This will be used for all future
     *                        document signing
     * @returns a list of decks, one per issuer.
     */
    async registerIdentity(keyProvider) {
        // Create the okapi request plenum document.
        // The payload payload for the document is the key algorithm and base64 encoded key bytes returned by the keyProvider.
        // The KeyID for the initial RegisterIdentity call, the KeyID must be all 0's. After that, the KeyID in the deck
        // should be used for future plenum documents.
        const okapiRequestDocument = await this.plenumClient.createPlenumDocument({
            keyId: bytesToEncoded(new Uint8Array(48)),
            keypair: keyProvider.getPrivateCryptoKey() ? createInlineKeyPair() : await keyProvider.getPlenumKeyPair(),
            data: await keyProvider.getEncodedPublicKey(),
        }, keyProvider.getPrivateCryptoKey());
        const okapiRequest = this.plenumClient.decodePlenumDocument(okapiRequestDocument.document);
        return await Promise.all(Object.values(this.okapiClients).map(async (client) => {
            const resp = await client.send(new RegisterIdentityCommand({
                payload: okapiRequest
            }));
            return resp.deck;
        }));
    }
    /**
     * Extracts the KeyID from a given deck.
     *
     * @argument deck: The deck to extract the KeyID from.
     * @returns the KeyID for the given deck. Used to identify the principal in all future plenum documents.
     */
    async extractKeyId(deck) {
        const parsedDeck = await this.plenumClient.parsePlenumDocument({ document: bytesToEncoded(deck) });
        const deckJson = JSON.parse(window.atob(parsedDeck.data));
        const principalCard = JSON.parse(deckJson['principal']);
        const encodedKeyId = principalCard['KeyID'];
        return this.plenumClient.decodePlenumDocument(encodedKeyId);
    }
    /**
     * Fetches trust roots from multiple issuers across different regions.
     *
     * @returns List of Uint8Array containing issuer states from different regions
     * @throws Error if no issuer states could be fetched
     */
    async fetchTrustRootsFromIssuers() {
        const issuerStates = [];
        await Promise.all(Object.entries(this.okapiClients).map(async ([endpoint, client]) => {
            const response = await client.send(new GetIssuerStateCommand({}));
            issuerStates.push(response.issuerState);
            console.log(`Successfully fetched issuer state from endpoint ${endpoint}`);
        }));
        return issuerStates;
    }
    getRegions(stage) {
        switch (stage.toLowerCase()) {
            case 'beta':
                return OKAPI_PREPROD_REGIONS;
            case 'gamma':
                return OKAPI_PREPROD_REGIONS;
            case 'prod':
                return OKAPI_PROD_REGIONS;
            default:
                throw `unsupported stage ${stage}! Only beta, gamma, and prod currently supported`;
        }
    }
    getEndpoint(authType, stage, region) {
        return `https://issuer-${authType}-a.${stage}.${region}.okapi.piezo.aws.dev`;
    }
    getOkapiClient(authType, region, endpoint, credentials, signingName) {
        if (authType === "iam") {
            return new OkapiIssuerClient({
                endpoint,
                region,
                credentials,
                ...(signingName && { httpAuthSchemeProvider: createSigningNameOverrideProvider(signingName) }),
            });
        }
        else if (authType === "midway") {
            return new OkapiIssuerClient({
                endpoint,
                region: 'ignored',
                credentials: { accessKeyId: 'ignored', secretAccessKey: 'ignored' },
                requestHandler: new SentryFetchHttpHandler()
            });
        }
        else {
            throw `Unknown/unsupported AuthType: ${authType}`;
        }
    }
}
